import { Bell, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notifications as notificationsApi, type Notification } from '../api/resources.js';
import { cn } from '../lib/cn.js';
import { formatRelative } from '../lib/format.js';
import { Button } from './ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.js';

/**
 * O sino (PRD §63, §121): as notificações de verdade, não um contador solto.
 *
 * Abre com as mais recentes; clicar numa marca como lida e leva para onde ela aponta —
 * cobrança, consumo, webhooks, instâncias. O feed completo continua em /notifications.
 */
const SEVERITY_DOT: Record<Notification['severity'], string> = {
  CRITICAL: 'bg-danger',
  WARNING: 'bg-warning',
  INFO: 'bg-primary',
  RESOLVED: 'bg-success',
};

/** Para onde cada tipo de notificação leva. O prefixo do tipo é a área do console. */
export function destinationFor(type: string): string {
  if (type.startsWith('billing.')) return '/billing';
  if (type.startsWith('usage.')) return '/usage';
  if (type.startsWith('webhook.')) return '/webhooks';
  if (type.startsWith('instance.')) return '/instances';
  return '/notifications';
}

export function NotificationBell({
  items,
  onChange,
}: {
  items: Notification[];
  onChange: () => void;
}) {
  const unread = items.filter((item) => item.readAt === null).length;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={unread > 0 ? `Notificações, ${unread} não lidas` : 'Notificações'}
          className="relative text-foreground"
        >
          <Bell />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 size-2 rounded-full bg-danger ring-2 ring-card"
              aria-hidden
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] max-w-[calc(100vw-24px)] p-0">
        <NotificationList items={items} onChange={onChange} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * O conteúdo do sino, separado do menu: é o que tem comportamento (marcar lida,
 * navegar, marcar todas) e é o que se testa. O menu em volta é só o Radix.
 */
export function NotificationList({
  items,
  onChange,
}: {
  items: Notification[];
  onChange: () => void;
}) {
  const navigate = useNavigate();
  const unread = items.filter((item) => item.readAt === null);
  const recent = items.slice(0, 6);

  const open = async (item: Notification) => {
    if (item.readAt === null) await notificationsApi.markRead(item.id).catch(() => undefined);
    onChange();
    navigate(destinationFor(item.type));
  };

  const readAll = async () => {
    await notificationsApi.markAllRead().catch(() => undefined);
    onChange();
  };

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2">
        <DropdownMenuLabel className="p-0">
          Notificações
          {unread.length > 0 && (
            <span className="ml-1.5 rounded-full bg-danger-soft px-1.5 text-[11px] font-semibold text-danger">
              {unread.length}
            </span>
          )}
        </DropdownMenuLabel>
        {unread.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => void readAll()}
          >
            <CheckCheck /> Marcar todas como lidas
          </Button>
        )}
      </div>
      <DropdownMenuSeparator className="my-0" />
      {recent.length === 0 ? (
        <p className="m-0 px-3 py-6 text-center text-sm text-muted-foreground">
          Nada por aqui. Você será avisado sobre consumo, cobrança, webhooks e instâncias.
        </p>
      ) : (
        <ul className="m-0 max-h-[380px] list-none overflow-y-auto p-1">
          {recent.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full cursor-pointer items-start gap-2.5 rounded-sm px-2 py-2 text-left outline-none transition-colors hover:bg-muted focus-visible:bg-muted"
                onClick={() => void open(item)}
              >
                <span
                  className={cn('mt-1.5 size-2 shrink-0 rounded-full', SEVERITY_DOT[item.severity])}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-[13px]',
                      item.readAt === null ? 'font-semibold' : 'font-normal',
                    )}
                  >
                    {item.title}
                  </span>
                  {item.body && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.body}
                    </span>
                  )}
                  <span className="block text-[11px] text-subtle">
                    {formatRelative(item.createdAt)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <DropdownMenuSeparator className="my-0" />
      <Button
        variant="ghost"
        size="sm"
        className="w-full rounded-t-none text-xs text-primary"
        onClick={() => navigate('/notifications')}
      >
        Ver todas
      </Button>
    </div>
  );
}
